import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { cn } from '../../lib/cn';
import { Empty, Select, Spinner } from '../../components/desktop';
import { ChartLineUp } from '@phosphor-icons/react';
import {
  deriveConditionOptions,
  deriveFinishOptions,
  pickDefaultSelection,
  resolveSubType,
  transformData,
} from './series';

/**
 * Price history, the converted `DetailsChart`.
 *
 * This is the same chart the app already shipped and the same `series.js`
 * behind it, so its tests carry over unchanged. What changed is everything
 * visual: the old one hardcoded `#00C49F`, `rgba(255,255,255,0.7)` and a
 * near-black tooltip, which made it a dark-theme-only chart sitting on a light
 * page. Not one colour is stated here now.
 *
 * WHY THE COLOURS ARE IN CSS AND NOT IN PROPS
 * -------------------------------------------
 * `var()` does not resolve inside an SVG PRESENTATION ATTRIBUTE, so
 * `stroke="var(--accent-primary)"` silently renders black. It does resolve in
 * a CSS declaration, so every colour here is applied by a rule in `detail.css`
 * reaching the elements recharts renders. That is also why the gradient stops
 * are coloured with the `stop-color` property rather than the `stop-color`
 * attribute.
 *
 * The line is ACCENT: a trend line is state, the reading of a thing, not a
 * command. Ink is reserved for the buttons.
 *
 * Amounts are Rands. The Y axis prefixes `R`, the tooltip is formatted by the
 * caller's `formatValue`, and neither may ever emit a dollar sign.
 */

const RANGE_MONTHS = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12 };

function normalise(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      return Array.isArray(parsed) ? parsed : parsed.marketHistory || [];
    } catch {
      return [];
    }
  }
  if (typeof input === 'object') {
    if (Array.isArray(input.marketHistory)) return input.marketHistory;
    if (typeof input.marketHistory === 'string') {
      try {
        return JSON.parse(input.marketHistory);
      } catch {
        return [];
      }
    }
  }
  return [];
}

function ChartTooltip({ active, payload, label, formatValue }) {
  if (!active || !payload || payload.length === 0) return null;

  let when = label;
  try {
    when = new Date(label).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (error) {
    when = label;
  }

  return (
    <div className="dsa-chart__tip">
      <p className="dsa-chart__tip-date">{when}</p>
      {payload.map((entry) => (
        <p className="dsa-chart__tip-row" key={entry.dataKey}>
          {entry.name || entry.dataKey}: <strong>{formatValue(entry.value)}</strong>
        </p>
      ))}
    </div>
  );
}

export function PriceHistory({
  marketHistory = [],
  /* CONTROLLED SELECTION. Supplying BOTH `finish` and `condition` hands the
     selection to the parent -- the card page does this so its headline price
     and this chart can never disagree about which condition they are showing,
     which is the defect this whole change exists to fix. `null` is a legal
     controlled value (a card with no priced rows), so presence is tested with
     `!== undefined`, not truthiness.

     In controlled mode this component renders NO selectors: the page renders
     them, above, next to the figure they also drive. It therefore never needs
     to report a selection UPWARD, and takes no `onSelectionChange`. One was
     carried here originally and never invoked, which read as wired and was
     not. */
  finish: finishProp,
  condition: conditionProp,
  pending = false,
  currentPrice,
  delta,
  height = 240,
  /** Formats a number as a Rand string. Injected so the pattern owns no money. */
  // "No data" rather than a dash: em dashes are banned product wide, and in a
  // price column a bare dash reads as zero rather than as a gap in the series.
  formatValue = (value) => (value == null ? 'No data' : `R${Number(value).toFixed(2)}`),
  /** Formats an axis tick. Coarser than `formatValue`, no decimals. */
  formatAxis = (value) => `R${Math.round(Number(value) || 0)}`,
  className,
}) {
  const rows = normalise(marketHistory);
  const allData = transformData(rows);

  const spanInMonths = React.useMemo(() => {
    if (!allData.length) return 0;
    const first = new Date(allData[0].date);
    const last = new Date(allData[allData.length - 1].date);
    return Math.ceil(Math.abs(last - first) / (1000 * 60 * 60 * 24 * 30));
  }, [allData]);

  const ranges = React.useMemo(() => {
    const out = [];
    if (spanInMonths >= 1) out.push('1M');
    if (spanInMonths >= 3) out.push('3M');
    if (spanInMonths >= 6) out.push('6M');
    if (spanInMonths >= 12) out.push('1Y');
    if (out.length === 0 && allData.length > 0) out.push('ALL');
    return out;
  }, [spanInMonths, allData.length]);

  const [range, setRange] = React.useState(() => (ranges.includes('3M') ? '3M' : ranges[0] || 'ALL'));

  const finishOptions = deriveFinishOptions(rows);
  const [finishState, setFinishState] = React.useState(() => pickDefaultSelection(rows).finish);
  const [conditionState, setConditionState] = React.useState(() => pickDefaultSelection(rows).condition);

  const controlled = finishProp !== undefined && conditionProp !== undefined;
  const finish = controlled ? finishProp : finishState;
  const condition = controlled ? conditionProp : conditionState;

  const conditionOptions = deriveConditionOptions(rows, finish);

  // Reconcile the selection against the data currently in hand. Navigating from
  // one card to another without a remount otherwise leaves a stale finish, and
  // the chart plots nothing. This is the "adjust state during render" pattern
  // keyed on VALUE validity rather than prop identity, so it self-terminates as
  // soon as the selection is valid and cannot loop even though `marketHistory`
  // defaults to a fresh array on every render.
  /* SKIPPED ENTIRELY WHEN CONTROLLED. This block adjusts state during render to
     repair a selection the data cannot support -- correct when this component
     owns the selection, and wrong when it does not. Under a controlled parent
     it would either write to state nothing reads (a silent no-op that leaves
     the invalid selection standing) or, if wired to a parent callback, fight
     the parent into "Too many re-renders". The parent is told what it selected
     was unplottable by the empty state below, and may act on that or not. */
  if (!controlled) {
    if (finishOptions.length > 0 && !finishOptions.includes(finish)) {
      const next = pickDefaultSelection(rows);
      setFinishState(next.finish);
      setConditionState(next.condition);
    } else if (finish != null && conditionOptions.length > 0 && !conditionOptions.includes(condition)) {
      setConditionState(conditionOptions[0] ?? null);
    }
  }

  const subtype = resolveSubType(rows, finish, condition);

  const displayData = React.useMemo(() => {
    if (!allData.length || range === 'ALL') return allData;
    const end = new Date(`${allData[allData.length - 1].date}T00:00:00Z`);
    const cutoff = new Date(end);
    cutoff.setMonth(cutoff.getMonth() - (RANGE_MONTHS[range] || 3));
    return allData.filter((row) => new Date(row.date) >= cutoff);
  }, [allData, range]);

  if (pending) {
    return (
      <div className={cn('dsa-chart', className)} aria-busy="true" style={{ height }}>
        <Spinner center>Loading price history</Spinner>
      </div>
    );
  }

  /* NO DATA AT ALL -- a sealed box, or a card the catalogue has never priced.
     The original sentence, and it is true here. */
  if (!allData.length) {
    return (
      <Empty size="sm" inline icon={ChartLineUp} title="No price history available"
        text="This card has no recorded sales yet." className={className} />
    );
  }

  /* DATA, BUT NOT FOR THIS PAIR -- ANYWHERE, NOT JUST IN THE SELECTED RANGE.
     8% of priced pairs. Saying "no recorded sales yet" here would be false --
     the card has plenty, just not at this grade -- and saying nothing at all
     leaves an empty set of axes that reads as a bug. So it names the
     condition it could not draw, which also tells the buyer the drop-down is
     what changes it.

     KEYED ON `allData`, NOT `displayData`. History is pruned at 180 days and
     the selected range defaults to 3M, so a pair whose series is real but
     ended more than 3 months before the card's newest point has nothing in
     `displayData` even though it has plenty in `allData`. Keying this guard
     on the range-filtered set told the buyer "no recorded sales to chart" for
     a pair that in fact has a chart, and did it below the range buttons
     (:318), so 6M/ALL were unreachable and the buyer had no way to see it was
     wrong. `allData` means this empty state now says only what it should:
     this pair has no series ANYWHERE. A pair whose current window happens to
     be empty falls through to the chart, where the range buttons are live. */
  if (!subtype || !allData.some((row) => row[subtype] != null)) {
    return (
      <Empty size="sm" inline icon={ChartLineUp}
        title={`No price history for ${condition || 'this condition'}`}
        text="We have a current price for it, but no recorded sales to chart."
        className={className} />
    );
  }

  const formatTick = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-ZA', {
      day: displayData.length <= 30 ? 'numeric' : undefined,
      month: 'short',
      year: displayData.length <= 30 ? undefined : 'numeric',
    });
  };

  return (
    <div className={cn('dsa-chart', className)}>
      {currentPrice || delta ? (
        <div className="dsa-chart__head">
          {currentPrice ? <span className="dsa-chart__now">{currentPrice}</span> : null}
          {delta}
        </div>
      ) : null}

      {/* THE LIBRARY `Select`, not a native one. These were the last two
          `<select class="dsk-input">` on the card detail page: the listings
          toolbar above them moved to the custom control, and leaving these
          native meant the same page opened an OS popup for two of its four
          dropdowns. `Select` takes `{value,label}` options and hands its
          `onChange` a plain string rather than an event, which is why the
          finish handler reads `next` directly.

          The comment sits HERE and not inside the ternary below, because a JSX
          comment is CHILD syntax and a ternary branch is an expression slot,
          where the same braces parse as an object literal instead.

          And it does NOT quote the comment tokens to say so, which is how this
          note previously broke: spelling the closing token out inside the body
          ENDS THE COMMENT THERE, and the rest of the sentence became text
          children rendered under the chart on both tracks. A block comment
          cannot contain its own terminator, so this one describes it instead.
          See the regression test in PriceHistory.test.jsx. */}
      {!controlled && finishOptions.length > 0 ? (
        <div className="dsa-chart__selectors">
          <Select
            label="Finish"
            options={finishOptions.map((option) => ({ value: option, label: option }))}
            value={finish ?? ''}
            onChange={(next) => {
              const conds = deriveConditionOptions(rows, next);
              setFinishState(next);
              if (!conds.includes(condition)) setConditionState(conds[0] ?? null);
            }}
          />
          <Select
            label="Condition"
            options={conditionOptions.map((option) => ({ value: option, label: option }))}
            value={condition ?? ''}
            onChange={(next) => setConditionState(next)}
          />
        </div>
      ) : null}

      <div className="dsa-chart__plot">
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={displayData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              {/* Stops are coloured by `stop-color` in CSS, not by an attribute,
                  so the fill follows the theme. See the note at the top. */}
              <linearGradient id="dsaChartFill" x1="0" y1="0" x2="0" y2="1">
                <stop className="dsa-chart__stop dsa-chart__stop--top" offset="0%" />
                <stop className="dsa-chart__stop dsa-chart__stop--bottom" offset="100%" />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} />
            <XAxis dataKey="date" tickFormatter={formatTick} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={formatAxis} axisLine={false} tickLine={false} width={62} />
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: 'none' }}
              content={<ChartTooltip formatValue={formatValue} />}
            />

            {subtype ? (
              <Area
                className="dsa-chart__area"
                type="monotone"
                dataKey={subtype}
                fill="url(#dsaChartFill)"
                fillOpacity={1}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2 }}
                connectNulls
              />
            ) : null}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {ranges.length > 1 ? (
        <div className="dsa-chart__ranges" role="group" aria-label="Chart range">
          {ranges.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === range}
              className={cn('dsa-chart__range', option === range && 'is-active')}
              onClick={() => setRange(option)}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default PriceHistory;
